import { useEffect, useMemo, useState } from "react";
import {
  Loader2, TrendingUp, Activity, Wrench, Sparkles, Info, ListTodo, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { filterInTimeframe } from "@/components/TimeframePicker";

/**
 * PerformancePanel — 4 high-value fleet KPIs.
 *  1. Teeth Life Cycle (New → 25 → 50 → 75 → New): average days per set.
 *  2. Asset Availability: % of fleet ready to work right now.
 *  3. MTTR (Mean Time to Repair): average hours to fix a breakdown.
 *  4. Maintenance Backlog: sum of open PPF estimated hours.
 *
 * All numbers are computed defensively from prod endpoints; if we can't
 * derive a metric we show "—" so a fake number never appears on-screen.
 *
 * `timeframe` (optional): { start, end, label } — filters time-sensitive
 * metrics (MTTR, Teeth cycle events) to that window. Availability is a live
 * snapshot; Backlog is queue-current — both stay untouched.
 */
export default function PerformancePanel({ timeframe }) {
  const [busy, setBusy] = useState(true);
  const [data, setData] = useState({});

  useEffect(() => {
    (async () => {
      const [dashRes, logsRes, assetsRes, bdRes, ppfRes] = await Promise.allSettled([
        api.get("/bucket-dashboard"),
        api.get("/bucket-logs"),
        api.get("/assets"),
        api.get("/breakdowns"),
        api.get("/ppfs"),
      ]);
      const list = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value.data;
        return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
      };
      const dashObj = dashRes.status === "fulfilled" ? dashRes.value.data : null;
      const logs = list(logsRes);
      const assets = list(assetsRes);
      const breakdowns = list(bdRes);
      const ppfs = list(ppfRes);
      setData({ dashObj, logs, assets, breakdowns, ppfs });
      setBusy(false);
    })().catch(() => setBusy(false));
  }, []);

  const kpis = useMemo(() => {
    const scoped = timeframe
      ? {
          ...data,
          logs: filterInTimeframe(data.logs, timeframe, ["created_at", "ts", "at"]),
          breakdowns: filterInTimeframe(data.breakdowns, timeframe, [
            "breakdown_time",
            "created_at",
            "reported_at",
          ]),
        }
      : data;
    return {
      teethCycle: computeTeethCycle(scoped),
      availability: computeAvailability(data),
      mttr: computeMttr(scoped),
      backlog: computeBacklog(data),
      pmCompliance: computePmCompliance(data),
    };
  }, [data, timeframe]);

  const rangeLabel = timeframe?.label || "this week";

  return (
    <section className="panel-heavy p-5 md:p-6" data-testid="performance-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
          <TrendingUp className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted">Performance</div>
          <div className="font-display font-black text-xl tracking-tight">Fleet KPIs · {rangeLabel}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi
          testid="kpi-teeth-cycle"
          icon={Sparkles}
          label="Teeth Life Cycle"
          sub="New → New (avg days)"
          value={kpis.teethCycle.value}
          suffix={kpis.teethCycle.suffix}
          hint={kpis.teethCycle.hint}
          tone="primary"
          spinner={busy}
        />
        <Kpi
          testid="kpi-availability"
          icon={Activity}
          label="Asset Availability"
          sub="Fleet ready to work"
          value={kpis.availability.value}
          suffix="%"
          hint={kpis.availability.hint}
          tone={availabilityTone(kpis.availability.value)}
          spinner={busy}
        />
        <Kpi
          testid="kpi-mttr"
          icon={Wrench}
          label="MTTR"
          sub="Hours to fix a breakdown"
          value={kpis.mttr.value}
          suffix="h"
          hint={kpis.mttr.hint}
          tone="blue"
          spinner={busy}
        />
        <Kpi
          testid="kpi-backlog"
          icon={ListTodo}
          label="Maintenance Backlog"
          sub="Man-hours queued (Planning Requests)"
          value={kpis.backlog.value}
          suffix="h"
          hint={kpis.backlog.hint}
          tone={backlogTone(kpis.backlog.value)}
          spinner={busy}
        />
        <Kpi
          testid="kpi-pm-compliance"
          icon={ShieldCheck}
          label="PM Compliance"
          sub="Preventives on-time"
          value={kpis.pmCompliance.value}
          suffix="%"
          hint={kpis.pmCompliance.hint}
          tone={pmComplianceTone(kpis.pmCompliance.value)}
          spinner={busy}
        />
      </div>

      <div className="text-[10px] uppercase tracking-widest text-muted mt-3 flex items-center gap-1">
        <Info className="w-3 h-3" /> Live from production data. Metrics that can&apos;t be derived show &mdash;.
      </div>
    </section>
  );
}

/* ---------------- KPI tile ---------------- */
function Kpi({ testid, icon: Icon, label, sub, value, suffix, hint, tone = "primary", spinner }) {
  const t = TONE[tone] || TONE.primary;
  const shown = value == null || Number.isNaN(value) ? "—" : formatNumber(value);
  return (
    <div className="panel p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
             style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}` }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-black text-muted truncate">{label}</div>
          <div className="text-[10px] text-muted truncate">{sub}</div>
        </div>
      </div>
      <div className="flex items-baseline gap-1">
        {spinner && value == null ? (
          <Loader2 className="w-6 h-6 animate-spin text-muted" />
        ) : (
          <span className="font-display font-black tracking-tight leading-none tabular-nums text-4xl" style={{ color: t.color }}>
            {shown}
          </span>
        )}
        {value != null && suffix && <span className="text-sm text-muted">{suffix}</span>}
      </div>
      {hint && <div className="text-[11px] text-muted mt-1 truncate">{hint}</div>}
    </div>
  );
}

const TONE = {
  primary: { bg: "rgba(245,130,32,0.12)", border: "rgba(245,130,32,0.35)", color: "var(--primary)" },
  green:   { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" },
  blue:    { bg: "#1a2a55", border: "#3b60d1", color: "#8bb4ff" },
  orange:  { bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
  red:     { bg: "#5a0f0f", border: "#a51919", color: "#ff9b9b" },
};

function availabilityTone(v) {
  if (v == null) return "primary";
  if (v >= 90) return "green";
  if (v >= 75) return "blue";
  if (v >= 60) return "orange";
  return "red";
}
function backlogTone(v) {
  if (v == null) return "primary";
  if (v <= 40) return "green";
  if (v <= 120) return "orange";
  return "red";
}
function pmComplianceTone(v) {
  if (v == null) return "primary";
  if (v >= 90) return "green";
  if (v >= 75) return "blue";
  if (v >= 60) return "orange";
  return "red";
}

/* ---------------- computations ---------------- */
function computeTeethCycle({ dashObj, logs } = {}) {
  // 1) Prefer the dashboard's aggregate if it exposes one
  const fromDash =
    firstNumberDeep(dashObj, [
      "avg_teeth_cycle_days", "average_teeth_cycle_days",
      "teeth_cycle_days_avg", "avg_cycle_days", "cycle_days_avg",
      "teeth_life_cycle_days", "teeth_new_to_new_days",
    ]);
  if (fromDash != null) {
    return { value: Math.round(fromDash), suffix: "d", hint: "avg per set · from backend" };
  }

  // 2) Compute from bucket-logs — look for "mark-new" events per asset,
  //    average the gap between consecutive NEW markers (days).
  if (Array.isArray(logs) && logs.length) {
    const byAsset = new Map();
    logs.forEach((l) => {
      const kind = String(l.event || l.kind || l.type || "").toLowerCase();
      const isNewEvent =
        kind.includes("mark-new") || kind.includes("mark_new") ||
        kind === "new" || kind === "reset" || kind.includes("reset") ||
        String(l.status || "").toLowerCase() === "new" ||
        l.reset === true;
      if (!isNewEvent) return;
      const ts = new Date(l.created_at || l.ts || l.at).getTime();
      if (!ts || isNaN(ts)) return;
      const aid = String(l.asset_id || l.assetId || "");
      if (!aid) return;
      if (!byAsset.has(aid)) byAsset.set(aid, []);
      byAsset.get(aid).push(ts);
    });
    const gaps = [];
    byAsset.forEach((arr) => {
      arr.sort((a, b) => a - b);
      for (let i = 1; i < arr.length; i++) gaps.push(arr[i] - arr[i - 1]);
    });
    if (gaps.length) {
      const avgMs = gaps.reduce((a, b) => a + b, 0) / gaps.length;
      const days = avgMs / 86400000;
      return {
        value: Math.round(days),
        suffix: "d",
        hint: `avg per set · ${gaps.length} cycle${gaps.length === 1 ? "" : "s"} observed`,
      };
    }
  }

  return { value: null, suffix: "d", hint: "Not enough replacement history yet" };
}

function computeAvailability({ assets } = {}) {
  if (!Array.isArray(assets) || !assets.length) return { value: null, hint: "No asset list" };
  const total = assets.length;
  const down = assets.filter((a) => {
    const s = String(a.status || a.state || "").toLowerCase();
    return s.includes("breakdown") || s.includes("down") || s.includes("out-of-service") || s.includes("offline") || a.is_down === true;
  }).length;
  const up = total - down;
  const pct = Math.round((up / total) * 100);
  return { value: pct, hint: `${up} of ${total} ready` };
}

function computeMttr({ breakdowns } = {}) {
  if (!Array.isArray(breakdowns) || !breakdowns.length) return { value: null, hint: "No breakdowns logged" };
  const durations = [];
  breakdowns.forEach((b) => {
    const start = new Date(b.breakdown_time || b.created_at).getTime();
    const end = new Date(b.back_in_service_time || b.closed_at || 0).getTime();
    if (start && end && end > start) durations.push((end - start) / 3600000);
  });
  if (!durations.length) return { value: null, hint: "No completed repairs yet" };
  const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
  return {
    value: avg < 10 ? +avg.toFixed(1) : Math.round(avg),
    hint: `${durations.length} repair${durations.length === 1 ? "" : "s"} closed`,
  };
}

function computeBacklog({ ppfs } = {}) {
  if (!Array.isArray(ppfs)) return { value: null, hint: "Planning Request list unavailable" };
  const open = ppfs.filter((p) => {
    const s = String(p.status || p.stage || "").toLowerCase();
    return !s.includes("closed") && !s.includes("complete") && !s.includes("signed") && !s.includes("done") && !s.includes("resolved");
  });
  if (!open.length) return { value: 0, hint: "Backlog clear" };
  const hours = open.reduce((acc, p) => acc + (Number(p.estimated_hours || p.est_hours || p.hours || 0) || 0), 0);
  const withEst = open.filter((p) => Number(p.estimated_hours || p.est_hours || p.hours) > 0).length;
  return {
    value: Math.round(hours),
    hint: `${open.length} open Planning Request${open.length === 1 ? "" : "s"}${withEst ? ` · ${withEst} estimated` : ""}`,
  };
}

function computePmCompliance({ ppfs } = {}) {
  if (!Array.isArray(ppfs) || !ppfs.length) return { value: null, hint: "No PPF data" };
  const now = Date.now();
  let due = 0;   // has a due date in the past OR completed
  let onTime = 0;
  ppfs.forEach((p) => {
    const dueAt = p.due_at || p.due_date || p.target_completion;
    if (!dueAt) return;
    const dueMs = new Date(dueAt).getTime();
    if (isNaN(dueMs)) return;
    const status = String(p.status || p.stage || "").toLowerCase();
    const isDone = ["closed", "complete", "signed", "done", "resolved"].some((k) => status.includes(k));
    // Only count PPFs whose due date is already in the past OR done — i.e. "should be finished by now"
    if (dueMs > now && !isDone) return;
    due += 1;
    if (isDone) {
      const finishedAt = new Date(p.closed_at || p.completed_at || p.signed_at || p.updated_at || dueAt).getTime();
      if (finishedAt <= dueMs) onTime += 1;
    }
  });
  if (!due) return { value: null, hint: "No Planning Requests are due yet" };
  const pct = Math.round((onTime / due) * 100);
  return { value: pct, hint: `${onTime} of ${due} on-time` };
}

/* ---------------- utils ---------------- */
function firstNumberDeep(obj, keys) {
  if (!obj || typeof obj !== "object") return null;
  for (const k of keys) {
    if (obj[k] != null && !isNaN(Number(obj[k]))) return Number(obj[k]);
  }
  // Look one level deeper (dashObj.stats, .kpis, .performance, etc)
  for (const nestedKey of ["stats", "kpis", "performance", "metrics", "totals"]) {
    if (obj[nestedKey]) {
      const v = firstNumberDeep(obj[nestedKey], keys);
      if (v != null) return v;
    }
  }
  return null;
}
function formatNumber(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 10000) return `${(n / 1000).toFixed(1)}k`;
  if (Number.isInteger(n)) return String(n);
  return String(n);
}
